import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { LogOut, MoreHorizontal, Building2, CheckCircle, Clock, XCircle, Layers } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  available: { labelEs: 'Disponible', labelEn: 'Available', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  reserved: { labelEs: 'Reservado', labelEn: 'Reserved', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  sold: { labelEs: 'Vendido', labelEn: 'Sold', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

export default function AdminDashboard() {
  const { t, lang } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, reserved: 0, sold: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filterFloor, setFilterFloor] = useState('all');
  const [filterTower, setFilterTower] = useState('all');
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchData = useCallback(async () => {
    try {
      const towerParam = filterTower !== 'all' ? `?tower=${filterTower}` : '';
      const [aptsRes, statsRes] = await Promise.all([
        axios.get(`${API}/apartments${towerParam}`, { withCredentials: true }),
        axios.get(`${API}/stats${towerParam}`, { withCredentials: true }),
      ]);
      setApartments(aptsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [filterTower]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEdit = (apt) => {
    setEditingId(apt.id);
    setEditData({ status: apt.status, price: apt.price, area_m2: apt.area_m2, bathrooms: apt.bathrooms });
  };

  const handleSave = async (id) => {
    try {
      await axios.put(`${API}/apartments/${id}`, editData, { withCredentials: true });
      toast.success(lang === 'es' ? 'Apartamento actualizado' : 'Apartment updated');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleFloorplanUpload = async (aptId, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/apartments/${aptId}/floorplan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      toast.success(lang === 'es' ? 'Plano subido' : 'Floor plan uploaded');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const filteredApts = filterFloor === 'all'
    ? apartments
    : apartments.filter(a => a.floor === parseInt(filterFloor));

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F5F7] pt-24 pb-16">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1
              className="text-3xl md:text-4xl font-light tracking-tight mb-1"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}
            >
              {t('admin.title')}
            </h1>
            <p className="text-sm text-[#8A828F]">
              {t('admin.welcome')}, {user?.name}
            </p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-[#8A828F] hover:text-[#C62828] transition-colors text-sm tracking-[0.1em] uppercase"
          >
            <LogOut size={16} />
            {t('admin.logout')}
          </button>
        </div>
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => navigate('/admin/structure')}
            data-testid="go-to-structure-btn"
            className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-[#D4AF37] hover:text-white transition-all"
          >
            <Layers size={14} />
            {lang === 'es' ? 'Estructura del Edificio' : 'Building Structure'}
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => navigate('/admin/cms')}
              data-testid="go-to-cms-btn"
              className="flex items-center gap-2 bg-[#D4AF37] text-white px-6 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-[#B5952F] transition-colors"
            >
              <Building2 size={14} />
              {lang === 'es' ? 'Ir a Gestión de Contenido' : 'Go to Content Management'}
            </button>
          )}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
        >
          {[
            { label: t('admin.total'), value: stats.total, icon: Building2, color: '#8A828F' },
            { label: t('admin.available'), value: stats.available, icon: CheckCircle, color: '#2E7D32' },
            { label: t('admin.reserved'), value: stats.reserved, icon: Clock, color: '#D4AF37' },
            { label: t('admin.sold'), value: stats.sold, icon: XCircle, color: '#C62828' },
          ].map(stat => (
            <div
              key={stat.label}
              data-testid={`stat-${stat.label.toLowerCase()}`}
              className="bg-white border border-[#E5E5E5] p-6"
            >
              <div className="flex items-center gap-2 mb-3">
                <stat.icon size={16} style={{ color: stat.color }} />
                <span className="text-xs tracking-[0.1em] uppercase text-[#8A828F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {stat.label}
                </span>
              </div>
              <p className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: stat.color }}>
                {stat.value}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#E5E5E5]"
        >
          <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-3">
            <h2
              className="text-xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t('admin.manageTitle')}
            </h2>
            <div className="flex items-center gap-3">
              <Select value={filterTower} onValueChange={setFilterTower}>
                <SelectTrigger className="w-[140px] border-[#E5E5E5] rounded-none" data-testid="tower-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'es' ? 'Todas las torres' : 'All towers'}</SelectItem>
                  <SelectItem value="kirei1">Kirei I</SelectItem>
                  <SelectItem value="kirei2">Kirei II</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterFloor} onValueChange={setFilterFloor}>
                <SelectTrigger className="w-[140px] border-[#E5E5E5] rounded-none" data-testid="floor-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'es' ? 'Todos los pisos' : 'All floors'}</SelectItem>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map(f => (
                    <SelectItem key={f} value={String(f)}>{t('admin.floor')} {f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E5E5]">
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{lang === 'es' ? 'Torre' : 'Tower'}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{t('admin.floor')}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{t('admin.unit')}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{t('admin.status')}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{t('admin.price')}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{t('admin.area')}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{lang === 'es' ? 'Baños' : 'Baths'}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F]">{lang === 'es' ? 'Plano' : 'Plan'}</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase text-[#8A828F] text-right">{t('admin.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApts.map(apt => (
                <TableRow key={apt.id} className="border-[#E5E5E5]" data-testid={`apt-row-${apt.id}`}>
                  <TableCell className="text-xs text-[#8A828F]">{apt.tower === 'kirei1' ? 'Kirei I' : 'Kirei II'}</TableCell>
                  <TableCell className="font-medium">{apt.floor}</TableCell>
                  <TableCell>{apt.unit}</TableCell>
                  <TableCell>
                    {editingId === apt.id ? (
                      <Select value={editData.status} onValueChange={(val) => setEditData(prev => ({ ...prev, status: val }))}>
                        <SelectTrigger className="w-[130px] h-8 rounded-none border-[#E5E5E5]" data-testid={`edit-status-${apt.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">{lang === 'es' ? 'Disponible' : 'Available'}</SelectItem>
                          <SelectItem value="reserved">{lang === 'es' ? 'Reservado' : 'Reserved'}</SelectItem>
                          <SelectItem value="sold">{lang === 'es' ? 'Vendido' : 'Sold'}</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${statusConfig[apt.status]?.color} border text-xs`}>
                        {lang === 'es' ? statusConfig[apt.status]?.labelEs : statusConfig[apt.status]?.labelEn}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === apt.id ? (
                      <Input
                        type="number"
                        value={editData.price}
                        onChange={(e) => setEditData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                        className="w-[120px] h-8 rounded-none border-[#E5E5E5]"
                        data-testid={`edit-price-${apt.id}`}
                      />
                    ) : (
                      formatPrice(apt.price)
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === apt.id ? (
                      <Input
                        type="number"
                        value={editData.area_m2}
                        onChange={(e) => setEditData(prev => ({ ...prev, area_m2: parseFloat(e.target.value) }))}
                        className="w-[100px] h-8 rounded-none border-[#E5E5E5]"
                        data-testid={`edit-area-${apt.id}`}
                      />
                    ) : (
                      `${apt.area_m2} m²`
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === apt.id ? (
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={editData.bathrooms}
                        onChange={(e) => setEditData(prev => ({ ...prev, bathrooms: parseFloat(e.target.value) }))}
                        className="w-[80px] h-8 rounded-none border-[#E5E5E5]"
                        data-testid={`edit-baths-${apt.id}`}
                      />
                    ) : (
                      apt.bathrooms
                    )}
                  </TableCell>
                  <TableCell>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,.svg,.pdf"
                        className="hidden"
                        data-testid={`upload-plan-${apt.id}`}
                        onChange={(e) => handleFloorplanUpload(apt.id, e.target.files?.[0])}
                      />
                      {apt.floorplan_image && !apt.floorplan_image.startsWith('/floorplan/') && apt.floorplan_image !== '' ? (
                        <span className="text-[10px] text-[#2E7D32] border border-[#2E7D32]/30 px-2 py-1 hover:bg-[#2E7D32]/10 transition-colors">
                          {lang === 'es' ? 'Cambiar' : 'Change'}
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1 hover:bg-[#D4AF37]/10 transition-colors">
                          {lang === 'es' ? 'Subir' : 'Upload'}
                        </span>
                      )}
                    </label>
                  </TableCell>
                  <TableCell className="text-right">
                    {editingId === apt.id ? (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleSave(apt.id)}
                          data-testid={`save-btn-${apt.id}`}
                          className="text-xs bg-[#D4AF37] text-white px-4 py-1.5 tracking-[0.1em] uppercase hover:bg-[#B5952F] transition-colors"
                        >
                          {t('admin.save')}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          data-testid={`cancel-btn-${apt.id}`}
                          className="text-xs border border-[#E5E5E5] text-[#8A828F] px-4 py-1.5 tracking-[0.1em] uppercase hover:text-[#1A1A1A] transition-colors"
                        >
                          {t('admin.cancel')}
                        </button>
                      </div>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`actions-btn-${apt.id}`} className="p-2 hover:bg-[#F5F5F7] transition-colors">
                            <MoreHorizontal size={16} className="text-[#8A828F]" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(apt)} data-testid={`edit-action-${apt.id}`}>
                            {t('admin.edit')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      </div>
    </div>
  );
}
